
import moment from 'moment'
import DB from './DB'

const SHOULD_SYNC_INTERVAL = 300 // 5 minutes expressed in seconds

const CATALOG_MODELS = [
  'CombinationGroupMenuItem',
  'DishOrder',
  'ItemSellingFormatPivot',
  'LinkedItemPivot',
  'MenuCategory',
  'MenuGroup',
  'MenuItem',
  'MenuMenuCategory',
  'MenuMenuPivot',
  'Modifier',
  'ModifierCategory',
  'ModifierGroup',
  'ModifierPivot',
  'SellingFormat',
]

const RUMMAGE_MODELS = [
  'Business',
  'Cashier',
  'Customer',
  'Discount',
  'FiscalSettings',
  'Integration',
  'KitchenNote',
  'OrderConfig',
  'PaymentMethod',
  'Permission',
  'Price',
  'PrintTicket',
  'TablePrice',
  'TableRoom',
  'TableTable',
  'Tax',
  'TenantUser',
]

const SYNC_MODELS = [
  ...CATALOG_MODELS,
  ...RUMMAGE_MODELS,
]

export default {
  mixins: [
    DB,
  ],

  data: () => ({
    shouldSyncTimer: null,
    veryOldDate: '1977-03-24 09:00:00', // my birthday ;)
  }),

  computed: {
    synchronizing: {
      get () {
        return this.$store.state.sync.synchronizing
      },
      set (v) {
        this.$store.commit('sync/synchronizing', v)
      }
    },
    shouldSynchronize: {
      get () {
        return this.$store.state.sync.shouldSynchronize
      },
      set (v) {
        this.$store.commit('sync/shouldSynchronize', v)
      }
    },
    screenBlocked () {
      return this.$store.state.user.screenBlocked
    },
    screenLocked () {
      return this.$store.state.user.screenLocked
    },
    appIsServiceable () {
      if (this.penicillinIsEnabled) {
        return false
      }
      return true
    },
    penicillinIsEnabled () {
      return !! this.$store.getters['orders/config']('penicillin_ip')
    },
  },

  methods: {
    syncFromDate () {
      // this variable can't be computed
      return localStorage.getItem('syncFrom') || this.veryOldDate
    },

    async syncModels () {
      let syncStatus
      try {
        syncStatus = await this.$api('getSyncStatus', { fromDate: this.syncFromDate() })
      } catch (e) {
        console.error(e)
        return []
      }

      const models = Object.entries(syncStatus.data)
        .filter(([model, sync]) => sync == '1' && SYNC_MODELS.indexOf(model) !== -1)
        .map(([model, sync]) => model)

      for (const model of SYNC_MODELS) {
        if (! await this.verifyChecksum(model)) {
          models.push(model)
        }
      }

      return models.unique()
    },

    async verifyChecksum (model) {
      const checksum = await this.db.get('checksum', model) ?? -1
      const count = await this.db.count(model)
      return count === checksum
    },

    async syncDatabase (loadIntoMemory = false) {
      const now = moment().utc().format('YYYY-MM-DD HH:mm:ss')
      const syncFromDate = this.syncFromDate()

      let synchronized = true
      for (const model of await this.syncModels()) {
        const fromDate = await this.verifyChecksum(model) ? syncFromDate : this.veryOldDate

        try {
          await this.syncModel(model, fromDate)
        } catch (e) {
          console.error(e)
          synchronized = false
        }

        if (loadIntoMemory) {
          await this.loadIntoMemory(model)
          this.afterSync(model)
        }
      }

      if (synchronized) {
        this.$log('Synchronized')
        this.shouldSynchronize = false
        localStorage.setItem('syncFrom', now)
      }
    },

    async syncModel (model, fromDate) {
      const response = await this.$api(['sync', model], { fromDate })

      await this.dbPutArray(model, response.data.new)
      await this.dbPutArray(model, response.data.updated)
      await this.dbDelArray(model, response.data.deleted)

      const count = await this.db.count(model)
      this.db.put('checksum', count, model)
    },

    startShouldSync () {
      this.shouldSyncTimer = setInterval(() => this.shouldSync(), SHOULD_SYNC_INTERVAL * 1000)
    },

    stopShouldSync () {
      clearInterval(this.shouldSyncTimer)
    },

    synchronizedRecently () {
      const lastSync = moment.utc(this.syncFromDate(), 'YYYY-MM-DD HH:mm:ss', true)
      return moment().utc().diff(lastSync, 'seconds') < SHOULD_SYNC_INTERVAL
    },

    async shouldSync () {
      if (this.screenBlocked || this.screenLocked || this.shouldSynchronize || this.synchronizedRecently()) {
        return
      }
      const models = await this.syncModels()
      this.shouldSynchronize = Boolean(models.length)
    },

    syncOrders () {
      return this.$store.dispatch('sync/orders')
    },

    async syncAll () {
      await this.syncOrders()
      await this.$store.dispatch('cashControl/getTurn').catch(e => console.warn(e))
      await this.syncDatabase(true)

      if (! this.appIsServiceable) {
        this.$store.commit('user/block')
      }
    },

    async loadIntoMemory (model) {
      const bank = CATALOG_MODELS.indexOf(model) !== -1 ? 'catalog' : 'rummage'
      const data = await this.db.getAll(model)
      this.$store.commit(`${bank}/set`, { model, data })
    },

    async loadAllIntoMemory () {
      for (const model of SYNC_MODELS) {
        await this.loadIntoMemory(model)
      }
      this.afterSync()
    },

    afterSync (model = null) {
      if ([null, 'MenuItem', 'LinkedItemPivot'].indexOf(model) !== -1) {
        this.$store.dispatch('catalog/searchable')
      }
      if ([null, 'Customer'].indexOf(model) !== -1) {
        this.$store.dispatch('customers/init')
      }
    },
  },
}
